import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Loader } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import Navigation from '../Home/Navigation';
import { SEO } from '../SEO';
import seoConfig from '../SEO/seoConfig';
import styles from './AudioGeneration.module.css';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/Auth/AuthContext';
import { uploadProjectMedia } from '../../services/mediaService';
import { getApiUrl } from '../../config/api';
import { isAdminUser } from '../../config/environment';
import { 
  fetchProjects, 
  Project, 
  deleteProject, 
  duplicateProject, 
  updateProjectStage,
  ProjectStage
} from '../../services/projectService';
import { projectService } from '../../services/projectService';
import { ServiceStatus } from './components/ServiceStatus';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faUpload, 
  faMicrophone, 
  faMusic, 
  faVolumeHigh,
  faCommentDots,
  faCalendarAlt, 
  faClock,
  faEllipsisVertical,
  faTrash,
  faDownload,
  faLink,
  faMagicWandSparkles,
  faHourglassHalf,
  faTimes,
  faCopy,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import AudioModal from './AudioModal';
import ExtractedAudioPreviewModal from './ExtractedAudioPreviewModal';
import { audioBufferToWav } from '../../utils/audio';

const AudioGeneration: React.FC = () => {
  const { t } = useTranslation('audioGeneration');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [isExtractingVideo, setIsExtractingVideo] = useState(false);
  const [selectedOption, setSelectedOption] = useState<'all' | 'recent' | 'audio'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [isProjectsLoading, setIsProjectsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeActionDropdown, setActiveActionDropdown] = useState<string | null>(null);
  
  // Modal states
  const [isAudioModalOpen, setIsAudioModalOpen] = useState(false);
  const [audioModalType, setAudioModalType] = useState<'upload' | 'narration' | 'music'>('upload');
  
  // Extracted audio preview modal states
  const [isExtractedAudioModalOpen, setIsExtractedAudioModalOpen] = useState(false);
  const [extractedAudioBlob, setExtractedAudioBlob] = useState<File | null>(null);
  const [extractedAudioUrl, setExtractedAudioUrl] = useState<string | null>(null);
  const [isUploadingExtractedAudio, setIsUploadingExtractedAudio] = useState(false);
  
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoFileInputRef = useRef<HTMLInputElement>(null);

  // Function to get audio duration from URL
  const getAudioDuration = async (audioUrl: string): Promise<string> => {
    try {
      return new Promise((resolve) => {
        const audio = new Audio();
        audio.addEventListener('loadedmetadata', () => {
          const duration = audio.duration;
          const minutes = Math.floor(duration / 60);
          const seconds = Math.floor(duration % 60);
          resolve(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        });
        
        audio.addEventListener('error', () => {
          resolve('0:00'); // Fallback if audio can't be loaded
        });
        
        audio.src = audioUrl;
      });
    } catch (error) {
      console.error('Error getting audio duration:', error);
      return '0:00';
    }
  };

  // Function to get project duration - prioritizes finalAudio over originalAudioUrl
  const getProjectDuration = async (project: Project): Promise<string> => {
    // If project already has a valid duration (not "00:00" or empty), use it
    if (project.duration && project.duration !== '00:00' && project.duration !== '0:00') {
      return project.duration;
    }
    
    // Otherwise, calculate from audio URL - prioritize finalAudio
    const audioUrl = project.finalAudio || project.originalAudioUrl;
    if (audioUrl) {
      return await getAudioDuration(audioUrl);
    }
    
    return '0:00';
  };

  // State to store calculated durations
  const [projectDurations, setProjectDurations] = useState<Record<string, string>>({});

  // Effect to calculate durations for projects
  useEffect(() => {
    const calculateDurations = async () => {
      const newDurations: Record<string, string> = {};
      
      for (const project of projects) {
        const duration = await getProjectDuration(project);
        newDurations[project.id] = duration;
      }
      
      setProjectDurations(newDurations);
    };

    if (projects.length > 0) {
      calculateDurations();
    }
  }, [projects]);

  // Fetch projects when component mounts
  useEffect(() => {
    let isMounted = true;
    
    const loadProjects = async () => {
      if (!isMounted) return;
      
      setIsProjectsLoading(true);
      setError(null);
      
      try {
        const response = await fetchProjects(1, 1000);
        
        if (!isMounted) return;
        
        if (response && 'projects' in response) {
          // Filter projects for audio generation - exclude projects that already have video
          const audioProjects = response.projects.filter(p => {
            // Exclude projects that have originalVideoUrl (video already uploaded, no audio needed)
            const hasVideoUrl = p.originalVideoUrl && p.originalVideoUrl.trim() !== '';
            if (hasVideoUrl) {
              return false; // Don't show video projects in audio generation
            }
            
            // Include projects that either:
            // 1. Have originalAudioUrl (existing audio projects)
            // 2. Are audio type projects
            // 3. Are in AUDIO stage
            // 4. Or other projects that need audio
            
            const hasAudioUrl = p.originalAudioUrl && p.originalAudioUrl.trim() !== '';
            const isAudioType = p.type === 'audio';
            const isAudioStage = p.stage === 'AUDIO' || p.stage === 'AUDIO_GENERATION';
            
            console.log('Project check:', {
              id: p.id,
              title: p.title,
              type: p.type,
              stage: p.stage,
              hasAudioUrl,
              hasVideoUrl,
              originalAudioUrl: p.originalAudioUrl,
              originalVideoUrl: p.originalVideoUrl,
              thumbnail: p.thumbnail,
              willShow: !hasVideoUrl && (hasAudioUrl || isAudioType || isAudioStage || true)
            });
            
            // Show projects that need audio (exclude video projects)
            return hasAudioUrl || isAudioType || isAudioStage || true; // Show all except video projects
          });
          
          setProjects(audioProjects);
          console.log('Filtered audio generation projects:', 
            audioProjects.map(p => ({
              id: p.id,
              title: p.title,
              type: p.type,
              stage: p.stage
            }))
          );
        } else {
          setProjects([]);
          console.warn('API returned unexpected data format');
        }
        
      } catch (err) {
        if (!isMounted) return;
        
        console.error('Error fetching projects:', err);
        setError(t('errors.loadProjects'));
        setProjects([]);
        
        // Only show notification if still mounted and not a network error during unmount
        if (isMounted) {
          showNotification({
            title: t('notifications.projectsLoadError.title'),
            message: t('notifications.projectsLoadError.message'),
            type: 'error'
          });
        }
      } finally {
        if (isMounted) {
          setIsProjectsLoading(false);
        }
      }
    };

    loadProjects();
    
    return () => {
      isMounted = false;
    };
  }, []); // ✅ Remove showNotification dependency to prevent re-runs

  // Handle file upload
  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      // Basic validation
      if (!file.type.startsWith('audio/')) {
        showNotification({
          title: t('notifications.invalidAudioFile.title'),
          message: t('notifications.invalidAudioFile.message'),
          type: 'error'
        });
        return;
      }
      
      setIsUploadingAudio(true);
      
      try {
        // Upload the file and create a project with track splitting enabled
        const uploadResult = await uploadProjectMedia([file], undefined, undefined, true);
        
        if (uploadResult.success && uploadResult.projectId) {
          // showNotification({
          //   title: 'Success',
          //   message: 'Audio file uploaded successfully! Redirecting to editor...',
          //   type: 'success'
          // });
          
          // Navigate to audio editor with the uploaded file
          setTimeout(() => {
            if (uploadResult.uploadedFiles && uploadResult.uploadedFiles.length > 0) {
              const audioUrl = encodeURIComponent(uploadResult.uploadedFiles[0].url);
              navigate(`/audio-editor/${uploadResult.projectId}?audioUrl=${audioUrl}`);
            } else {
              navigate(`/audio-editor/${uploadResult.projectId}`);
            }
          }, 1000);
        } else {
          throw new Error('Upload failed - no project ID returned');
        }
        
      } catch (error) {
        console.error("Upload failed:", error);
        showNotification({
          title: t('notifications.uploadFailed.title'),
          message: t('notifications.uploadFailed.message'),
          type: 'error'
        });
      } finally {
        setIsUploadingAudio(false);
      }
    }
  };

  // Handle video file upload for audio extraction
  const handleVideoFileUpload = () => {
    videoFileInputRef.current?.click();
  };

  const handleVideoFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      // Basic validation
      if (!file.type.startsWith('video/')) {
        showNotification({
          title: t('notifications.invalidVideoFile.title'),
          message: t('notifications.invalidVideoFile.message'),
          type: 'error'
        });
        return;
      }
      
      setIsExtractingVideo(true);
      
      try {
        // Extract audio from video file
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const videoArrayBuffer = await file.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(videoArrayBuffer);
        
        // Convert audio buffer to compressed WAV blob (Level 3 = 8-bit/32kHz, ~35MB for 3min)
        // This reduces file size by ~91% compared to uncompressed WAV
        const wavBlob = audioBufferToWav(audioBuffer, true, 3);
        const audioFile = new File([wavBlob], `${file.name.split('.')[0]}_audio.wav`, { type: 'audio/wav' });
        
        // Create preview URL for the extracted audio
        const previewUrl = URL.createObjectURL(wavBlob);
        
        // Store the blob and URL for the preview modal
        setExtractedAudioBlob(audioFile);
        setExtractedAudioUrl(previewUrl);
        
        // Open the preview modal
        setIsExtractedAudioModalOpen(true);
        setIsExtractingVideo(false);
        
      } catch (error) {
        console.error("Video extraction failed:", error);
        showNotification({
          title: t('notifications.extractionFailed.title'),
          message: t('notifications.extractionFailed.message'),
          type: 'error'
        });
        setIsExtractingVideo(false);
      }
    }
  };

  // Handle uploading the extracted audio from preview modal
  const handleUploadExtractedAudio = async () => {
    if (!extractedAudioBlob) return;
    
    setIsUploadingExtractedAudio(true);
    
    try {
      // Upload the extracted audio
      const uploadResult = await uploadProjectMedia([extractedAudioBlob], undefined, undefined, true);
      
      if (uploadResult.success && uploadResult.projectId) {
        // showNotification({
        //   title: 'Success',
        //   message: 'Audio extracted and uploaded successfully! Redirecting to editor...',
        //   type: 'success'
        // });
        
        // Clean up preview URL
        if (extractedAudioUrl) {
          URL.revokeObjectURL(extractedAudioUrl);
        }
        
        // Close the modal
        setIsExtractedAudioModalOpen(false);
        
        // Reset states
        setExtractedAudioBlob(null);
        setExtractedAudioUrl(null);
        
        // Navigate to audio editor with the extracted audio
        setTimeout(() => {
          if (uploadResult.uploadedFiles && uploadResult.uploadedFiles.length > 0) {
            const audioUrl = encodeURIComponent(uploadResult.uploadedFiles[0].url);
            navigate(`/audio-editor/${uploadResult.projectId}?audioUrl=${audioUrl}`);
          } else {
            navigate(`/audio-editor/${uploadResult.projectId}`);
          }
        }, 1000);
      } else {
        throw new Error('Upload failed - no project ID returned');
      }
      
    } catch (error) {
      console.error("Upload failed:", error);
      showNotification({
        title: 'Upload Failed',
        message: 'There was an error uploading the extracted audio',
        type: 'error'
      });
    } finally {
      setIsUploadingExtractedAudio(false);
    }
  };

  // Handle canceling the extracted audio preview
  const handleCancelExtractedAudio = () => {
    // Clean up preview URL
    if (extractedAudioUrl) {
      URL.revokeObjectURL(extractedAudioUrl);
    }
    
    // Reset states
    setIsExtractedAudioModalOpen(false);
    setExtractedAudioBlob(null);
    setExtractedAudioUrl(null);
  };

  // Handle downloading the extracted audio
  const handleDownloadExtractedAudio = () => {
    if (!extractedAudioBlob || !extractedAudioUrl) return;
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = extractedAudioUrl;
    link.download = extractedAudioBlob.name || 'extracted_audio.wav';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle modal open for different types
  const handleOpenModal = (type: 'upload' | 'narration' | 'music') => {
    setAudioModalType(type);
    setIsAudioModalOpen(true);
  };

  const handleAudioGenerated = (audioUrl: string, projectId: string, type: 'narration' | 'music') => {
    console.log('Audio generated:', { audioUrl, projectId, type });
    
    // Show success notification
    // showNotification({
    //   title: 'Success',
    //   message: `${type === 'narration' ? 'Narration' : 'Melody'} generated successfully! Redirecting to audio editor...`,
    //   type: 'success'
    // });
    
    // Close modal
    setIsAudioModalOpen(false);
    
    // Navigate to audio editor with the new audio
    setTimeout(() => {
      const encodedAudioUrl = encodeURIComponent(audioUrl);
      navigate(`/audio-editor/${projectId}?audioUrl=${encodedAudioUrl}`);
    }, 1000); // Small delay to let user see the success message
  };

  // Toggle dropdown visibility
  const toggleActionDropdown = (projectId: string) => {
    setActiveActionDropdown(activeActionDropdown === projectId ? null : projectId);
  };

  // Handle project delete
  const handleDeleteProject = async (projectId: string) => {
    try {
      await deleteProject(projectId);
      setProjects(projects.filter(project => project.id !== projectId));
      // showNotification({
      //   title: 'Success',
      //   message: 'Project deleted successfully',
      //   type: 'success'
      // });
    } catch (error) {
      console.error("Delete failed:", error);
      showNotification({
        title: t('notifications.deleteFailed.title'),
        message: t('notifications.deleteFailed.message'),
        type: 'error'
      });
    }
  };

  // Handle project duplication
  const handleDuplicateProject = async (projectId: string) => {
    try {
      const newProject = await duplicateProject(projectId);
      setProjects([newProject, ...projects]);
      // showNotification({
      //   title: 'Success',
      //   message: 'Project duplicated successfully',
      //   type: 'success'
      // });
    } catch (error) {
      console.error("Duplication failed:", error);
      showNotification({
        title: t('notifications.duplicateFailed.title'),
        message: t('notifications.duplicateFailed.message'),
        type: 'error'
      });
    }
  };

  // Handle continue working on project
  const handleContinueProject = async (project: Project) => {
    console.log('Continue project:', { 
      projectId: project.id, 
      projectType: project.type, 
      originalAudioUrl: project.originalAudioUrl
    });
    
    try {
      // Update project stage to EDIT when navigating to edit page
      await updateProjectStage(project.id, ProjectStage.EDIT);
      
      // Navigate to audio editor with project ID and audio URL
      if (project.originalAudioUrl) {
        const audioUrl = encodeURIComponent(project.originalAudioUrl);
        navigate(`/audio-editor/${project.id}?audioUrl=${audioUrl}`);
      } else {
        // Navigate to audio editor even without existing audio
        navigate(`/audio-editor/${project.id}`);
        // showNotification({
        //   title: 'Audio Editor Opened',
        //   message: 'You can upload audio files to start editing.',
        //   type: 'info'
        // });
      }
      
    } catch (error) {
      console.error("Error updating project stage:", error);
      showNotification({
        title: t('notifications.projectAccessError.title'),
        message: t('notifications.projectAccessError.message'),
        type: 'error'
      });
    }
  };

  // Filter projects based on selectedOption and searchTerm  
  const filteredProjects = projects.filter(project => {
    // Filter by type
    if (selectedOption === 'audio' && project.type !== 'audio') return false;
    if (selectedOption === 'recent') {
      // Get projects from the last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const projectDate = new Date(project.date);
      if (projectDate < sevenDaysAgo) return false;
    }
    
    // Filter by search term
    if (searchTerm && !project.title.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  // Calculate counts for each filter
  const audioProjectsCount = projects.filter(project => project.type === 'audio').length;
  const recentProjectsCount = projects.filter(project => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const projectDate = new Date(project.date);
    return projectDate >= sevenDaysAgo;
  }).length;

  // Function to format the date in a more readable way
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className={styles.outerContainer}>
      <SEO
        title={seoConfig.audioGenerator.title}
        description={seoConfig.audioGenerator.description}
        keywords={seoConfig.audioGenerator.keywords}
        url="https://kingdomstudio.pro/audio-generation"
      />
      <Navigation />
      <main className={styles.container}>
        <div className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.header}>
              <div className={styles.titleContainer}>
                <h1 className={styles.pageTitle}>{t('page.title')}</h1>
                <div className={styles.projectNameContainer}>
                  <h2 className={styles.projectName}>{t('page.subtitle')}</h2>
                </div>
              </div>
            </div>
            <p className={styles.subtitle}>
              {t('page.description')}
            </p>

            {/* Service Status - Development Only */}
            {import.meta.env.DEV && <ServiceStatus compact={true} />}

            <div className={styles.createOptions}>
              <div className={styles.cardOption}>
                <div className={styles.iconContainer}>
                  <FontAwesomeIcon icon={faUpload} className={styles.optionIcon} />
                </div>
                <h3 className={styles.optionTitle}>{t('createOptions.uploadAudio.title')}</h3>
                <p className={styles.optionDesc}>
                  {t('createOptions.uploadAudio.description')}
                </p>
                <Button
                  className={styles.optionButton}
                  size="lg"
                  onClick={handleFileUpload}
                  disabled={isUploadingAudio || isExtractingVideo}
                  style={{
                    background: isUploadingAudio 
                      ? "linear-gradient(135deg, #6366f1, #a78bfa)" 
                      : "linear-gradient(135deg, #3b82f6, #8b5cf6)",
                    border: "none",
                    color: "white",
                    cursor: (isUploadingAudio || isExtractingVideo) ? "not-allowed" : "pointer",
                    opacity: (isUploadingAudio || isExtractingVideo) ? 0.8 : 1
                  }}
                >
                  {isUploadingAudio ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: "8px" }} />
                      {t('createOptions.uploadAudio.buttonUploading')}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faUpload} style={{marginRight: "8px"}} /> 
                      {t('createOptions.uploadAudio.button')}
                    </>
                  )}
                </Button>
              </div>

              <div className={styles.cardOption}>
                <div className={styles.iconContainer}>
                  <FontAwesomeIcon icon={faVolumeHigh} className={styles.optionIcon} />
                </div>
                <h3 className={styles.optionTitle}>{t('createOptions.extractAudio.title')}</h3>
                <p className={styles.optionDesc}>
                  {t('createOptions.extractAudio.description')}
                </p>
                <Button
                  className={styles.optionButton}
                  size="lg"
                  onClick={handleVideoFileUpload}
                  disabled={isUploadingAudio || isExtractingVideo}
                  style={{
                    background: isExtractingVideo 
                      ? "linear-gradient(135deg, #34d399, #60a5fa)" 
                      : "linear-gradient(135deg, #10b981, #3b82f6)",
                    border: "none",
                    color: "white",
                    cursor: (isUploadingAudio || isExtractingVideo) ? "not-allowed" : "pointer",
                    opacity: (isUploadingAudio || isExtractingVideo) ? 0.8 : 1
                  }}
                >
                  {isExtractingVideo ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: "8px" }} />
                      {t('createOptions.extractAudio.buttonExtracting')}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faVolumeHigh} style={{marginRight: "8px"}} /> 
                      {t('createOptions.extractAudio.button')}
                    </>
                  )}
                </Button>
              </div>

              <div className={styles.cardOption}>
                <div className={styles.iconContainer}>
                  <FontAwesomeIcon icon={faCommentDots} className={styles.optionIcon} />
                </div>
                <h3 className={styles.optionTitle}>{t('createOptions.generateNarration.title')}</h3>
                <p className={styles.optionDesc}>
                  {t('createOptions.generateNarration.description')}
                </p>
                <Button
                  className={styles.optionButton}
                  size="lg"
                  onClick={() => handleOpenModal('narration')}
                  disabled={isUploadingAudio || isExtractingVideo || isLoading || isUploadingExtractedAudio}
                  style={{
                    background: (isUploadingAudio || isExtractingVideo || isLoading || isUploadingExtractedAudio) 
                      ? "#666" 
                      : "linear-gradient(135deg, #8b5cf6, #e11d48)",
                    border: "none",
                    color: (isUploadingAudio || isExtractingVideo || isLoading || isUploadingExtractedAudio) 
                      ? "#ccc" 
                      : "white",
                    cursor: (isUploadingAudio || isExtractingVideo || isLoading || isUploadingExtractedAudio) 
                      ? "not-allowed" 
                      : "pointer",
                    opacity: (isUploadingAudio || isExtractingVideo) ? 0.6 : 1
                  }}
                >
                  <FontAwesomeIcon icon={faCommentDots} style={{marginRight: "8px"}} /> 
                  {t('createOptions.generateNarration.button')}
                </Button>
              </div>

              {/* Music Generation - Admin Only */}
              {isAdminUser(user) && (
                <div className={styles.cardOption}>
                  <div className={styles.iconContainer}>
                    <FontAwesomeIcon icon={faMusic} className={styles.optionIcon} />
                  </div>
                  <h3 className={styles.optionTitle}>{t('createOptions.generateMusic.title')}</h3>
                  <p className={styles.optionDesc}>
                    {t('createOptions.generateMusic.description')}
                  </p>
                  <Button
                    className={styles.optionButton}
                    size="lg"
                    onClick={() => handleOpenModal('music')}
                    disabled={isUploadingAudio || isExtractingVideo || isLoading || isUploadingExtractedAudio}
                    style={{
                      background: (isUploadingAudio || isExtractingVideo || isLoading || isUploadingExtractedAudio) 
                        ? "#666" 
                        : "linear-gradient(135deg, #f59e0b, #ec4899)",
                      border: "none",
                      color: (isUploadingAudio || isExtractingVideo || isLoading || isUploadingExtractedAudio) 
                        ? "#ccc" 
                        : "white",
                      cursor: (isUploadingAudio || isExtractingVideo || isLoading || isUploadingExtractedAudio) 
                        ? "not-allowed" 
                        : "pointer",
                      opacity: (isUploadingAudio || isExtractingVideo || isLoading || isUploadingExtractedAudio) ? 0.6 : 1
                    }}
                  >
                    <FontAwesomeIcon icon={faMusic} style={{marginRight: "8px"}} /> 
                    {t('createOptions.generateMusic.button')}
                  </Button>
                </div>
              )}
            </div>

            {/* Hidden file input */}
            <input
              type="file"
              accept="audio/*"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            
            {/* Hidden video file input for audio extraction */}
            <input
              type="file"
              accept="video/*"
              ref={videoFileInputRef}
              style={{ display: 'none' }}
              onChange={handleVideoFileChange}
            />
          </div>
        </div>
        
        {/* Projects Section */}
        <div className={styles.projectsSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('projectsSection.title')}</h2>
            <div className={styles.searchAndFilter}>
              <div className={styles.searchBar}>
                <input
                  type="text"
                  placeholder={t('filters.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
              
              <div className={styles.filterOptions}>
                <button 
                  className={`${styles.filterButton} ${selectedOption === 'all' ? styles.active : ''}`}
                  onClick={() => setSelectedOption('all')}
                >
                  {t('filters.all')} ({projects.length})
                </button>
                <button 
                  className={`${styles.filterButton} ${selectedOption === 'recent' ? styles.active : ''}`}
                  onClick={() => setSelectedOption('recent')}
                >
                  {t('filters.recent')} ({recentProjectsCount})
                </button>
                <button 
                  className={`${styles.filterButton} ${selectedOption === 'audio' ? styles.active : ''}`}
                  onClick={() => setSelectedOption('audio')}
                >
                  {t('filters.audio')} ({audioProjectsCount})
                </button>
              </div>
            </div>
          </div>
          
          {isProjectsLoading ? (
            <div className={styles.loadingContainer}>
              <Loader size="xl" />
              <p>{t('projectsSection.loading')}</p>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <p className={styles.errorMessage}>{error}</p>
              <Button onClick={() => window.location.reload()} className={styles.retryButton}>
                {t('errors.retryButton')}
              </Button>
            </div>
          ) : projects.length > 0 ? (
            <div className={styles.projectGrid}>
              {filteredProjects.map(project => (
                <div 
                  key={project.id} 
                  className={`${styles.projectCard} 
                    ${activeActionDropdown === project.id ? styles.cardWithActiveDropdown : ''}
                  `}
                >
                  <div className={styles.projectThumbnail}>
                    <img 
                      src={project.thumbnail && project.thumbnail.trim() !== '' ? project.thumbnail : getApiUrl('/public/assets/audio-waveform.svg')} 
                      alt={project.title}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        console.log('Image load error for project:', project.id, 'Original src:', target.src);
                        target.src = getApiUrl('/public/assets/audio-waveform.png');
                      }} 
                      onLoad={(e) => {
                        const target = e.target as HTMLImageElement;
                        console.log('Image loaded successfully for project:', project.id, 'src:', target.src);
                      }}
                    />
                    <div className={styles.projectType}>
                      <FontAwesomeIcon icon={faMicrophone} />
                    </div>
                    <div className={styles.projectDuration}>
                      <FontAwesomeIcon icon={faClock} />
                      <span>{projectDurations[project.id] || project.duration || '0:00'}</span>
                    </div>
                  </div>
                  
                  <div className={styles.projectInfo}>
                    <h3 className={styles.projectTitle}>{project.title}</h3>
                    <div className={styles.projectMeta}>
                      <span className={styles.projectDate}>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {formatDate(project.date)}
                      </span>
                    </div>
                  </div>
                  
                  <div className={styles.projectActions}>
                    <Button 
                      className={styles.continueButton}
                      onClick={() => handleContinueProject(project)}
                    >
                      {t('projectCard.continueButton')}
                    </Button>
                    <div className={styles.moreActions}>
                      <button 
                        className={styles.moreButton}
                        onClick={() => toggleActionDropdown(project.id)}
                      >
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                      </button>
                      {activeActionDropdown === project.id && (
                        <div className={`${styles.actionDropdown} ${activeActionDropdown === project.id ? styles.active : ''}`}
                             onClick={(e) => e.stopPropagation()}
                        >
                          {project.originalAudioUrl && (
                            <a 
                              className={styles.actionButton}
                              href={project.originalAudioUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                            >
                              <FontAwesomeIcon icon={faDownload} /> {t('projectCard.actions.downloadAudio')}
                            </a>
                          )}
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleDuplicateProject(project.id)}
                          >
                            {t('projectCard.actions.duplicate')}
                          </button>
                          <button className={styles.actionButton}>{t('projectCard.actions.share')}</button>
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleDeleteProject(project.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} /> {t('projectCard.actions.delete')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.noProjects}>
              <FontAwesomeIcon icon={faPlus} size="2x" className={styles.noProjectsIcon} />
              <h3>{t('projectsSection.noProjects.title')}</h3>
              <p>{t('projectsSection.noProjects.description')}</p>
              <Button 
                className={styles.createNewButton}
                onClick={() => handleOpenModal('narration')}
                leftSection={<FontAwesomeIcon icon={faMagicWandSparkles} />}
              >
                {t('projectsSection.noProjects.button')}
              </Button>
            </div>
          )}
        </div>
      </main>
      
      {/* Audio Generation Modal */}
      <AudioModal
        isOpen={isAudioModalOpen}
        onClose={() => setIsAudioModalOpen(false)}
        onAudioGenerated={handleAudioGenerated}
        type={audioModalType}
        hideAllPromptsTab={true} // Hide "All Prompts" tab on landing page
      />
      
      {/* Extracted Audio Preview Modal */}
      <ExtractedAudioPreviewModal
        isOpen={isExtractedAudioModalOpen}
        onClose={handleCancelExtractedAudio}
        onDownload={handleDownloadExtractedAudio}
        onUseAudio={handleUploadExtractedAudio}
        audioUrl={extractedAudioUrl}
        isLoading={isUploadingExtractedAudio}
      />
    </div>
  );
};

export default AudioGeneration;